(() => {
  if (window.__missQuickCartReady) return;
  window.__missQuickCartReady = true;

  const shopRoot = window.Shopify?.routes?.root || '/';
  const cartEndpoint = `${shopRoot}cart.js`;
  const cartAddEndpoint = `${shopRoot}cart/add.js`;
  const cartChangeEndpoint = `${shopRoot}cart/change.js`;

  const controls = () => [...document.querySelectorAll('[data-public-quantity-control]')];
  let latestQuantities = new Map();
  const pending = new Map();
  const errors = new Map();
  let latestCart = { items: [], item_count: 0 };
  let cartLoaded = false;
  let loadingCart;
  let draining;

  const syncControl = (control) => {
    if (!control) return;
    const id = String(control.dataset.variantId);
    const quantity = pending.get(id)?.quantity ?? latestQuantities.get(id) ?? 0;
    const error = errors.get(id);
    control.dataset.cartQuantity = String(quantity);
    control.dataset.busy = String(pending.has(id));
    control.dataset.error = String(Boolean(error));
    const output = control.querySelector('[data-public-quantity-output]');
    const decrement = control.querySelector('[data-public-quantity-change="-1"]');
    if (output) output.textContent = String(quantity);
    if (decrement) decrement.disabled = quantity < 1;
    const add = control.querySelector('[data-retail-add]');
    if (add) {
      add.disabled = pending.has(id);
      add.setAttribute('aria-busy', String(pending.has(id)));
      add.textContent = pending.has(id) ? add.dataset.loadingLabel : add.dataset.addLabel;
      if (error) add.textContent = add.dataset.addLabel;
    }
    const status = control.querySelector('[data-public-quick-add-status]');
    if (status) status.textContent = error || (pending.has(id) ? 'Actualizando la bolsa…' : (quantity > 0 ? 'Añadido a tu carrito' : ''));
  };

  const updateCartBubble = (itemCount) => {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    let bubble = cartLink.querySelector('.cart-count-bubble');
    if (itemCount < 1) {
      bubble?.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      const visible = document.createElement('span');
      visible.setAttribute('aria-hidden', 'true');
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      bubble.append(visible, hidden);
      cartLink.appendChild(bubble);
    }
    const visible = bubble.querySelector('[aria-hidden="true"]');
    const hidden = bubble.querySelector('.visually-hidden');
    if (visible) visible.textContent = itemCount < 100 ? String(itemCount) : '';
    if (hidden) hidden.textContent = `${itemCount} artículos en la bolsa`;
  };

  const fetchCart = async () => {
    const response = await fetch(cartEndpoint, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('No se ha podido actualizar la bolsa.');
    return response.json();
  };

  const renderControls = () => {
    controls().forEach(syncControl);
    let count = Number(latestCart.item_count || 0);
    pending.forEach((entry, id) => { count += entry.quantity - (latestQuantities.get(id) || 0); });
    updateCartBubble(Math.max(0, count));
  };

  const renderCart = (cart) => {
    const quantities = new Map();
    (cart.items || []).forEach((item) => {
      const variantId = String(item.variant_id);
      quantities.set(variantId, (quantities.get(variantId) || 0) + Number(item.quantity || 0));
    });
    latestQuantities = quantities;
    latestCart = cart;
    renderControls();
  };

  const ensureCart = () => {
    if (cartLoaded) return Promise.resolve();
    if (!loadingCart) loadingCart = fetchCart().then((cart) => {
      pending.forEach((entry, id) => {
        if (!entry.relative) return;
        const existing = cart.items.filter((item) => String(item.variant_id) === id)
          .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        entry.quantity = Math.max(0, entry.quantity + existing - entry.base);
        entry.relative = false;
      });
      cartLoaded = true;
      renderCart(cart);
    }).finally(() => { loadingCart = null; });
    return loadingCart;
  };

  const drain = () => {
    if (draining) return draining;
    draining = (async () => {
      while (pending.size) {
        try {
          await ensureCart();
        } catch (error) {
          pending.forEach((entry, id) => errors.set(id, error.message));
          pending.clear();
          renderControls();
          break;
        }
        const [id, entry] = pending.entries().next().value;
        const next = entry.quantity;
        const current = latestQuantities.get(id) || 0;
        if (next === current) {
          pending.delete(id);
          renderControls();
          continue;
        }
        try {
          const endpoint = current === 0 ? cartAddEndpoint : cartChangeEndpoint;
          const payload = current === 0
            ? { items: [{ id: Number(id), quantity: next }] }
            : { id, quantity: next };
          const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          });
          const body = await response.json();
          if (!response.ok) throw new Error(body.description || body.message || 'No se ha podido actualizar el modelo.');
          const cart = endpoint === cartChangeEndpoint ? body : await fetchCart();
          const actual = cart.items.filter((item) => String(item.variant_id) === id)
            .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
          if (actual !== next) {
            pending.delete(id);
            errors.set(id, 'No se ha podido actualizar el modelo.');
          } else if (entry.quantity === next) {
            pending.delete(id);
          }
          renderCart(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        } catch (error) {
          pending.delete(id);
          errors.set(id, error.message || 'No se ha podido actualizar el modelo.');
          try {
            renderCart(await fetchCart());
          } catch {
            cartLoaded = false;
          }
          renderControls();
        }
      }
    })().finally(() => { draining = null; });
    return draining;
  };

  const changeQuantity = (control, delta) => {
    const id = String(control.dataset.variantId);
    const current = pending.get(id)?.quantity ?? latestQuantities.get(id) ?? 0;
    const next = Math.max(0, current + delta);
    if (next === current) return;
    const entry = pending.get(id) || { relative: !cartLoaded, base: latestQuantities.get(id) || 0 };
    entry.quantity = next;
    pending.set(id, entry);
    errors.delete(id);
    renderControls();
    drain();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-public-quantity-change]');
    if (!button) return;
    event.preventDefault();
    const control = button.closest('[data-public-quantity-control]');
    if (!control) return;
    changeQuantity(control, Number(button.dataset.publicQuantityChange || 0));
  });

  document.addEventListener('miss:public-variant-changed', (event) => {
    syncControl(event.detail?.control);
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || (!pending.size && !draining) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/\/(cart|checkout)\/?$/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    link.setAttribute('aria-busy', 'true');
    drain().then(() => { window.location.assign(link.href); });
  }, true);

  const init = () => {
    if (!controls().length) return;
    ensureCart().catch(() => {});
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
